import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { lockBodyScroll } from '../../utils/bodyScrollLock';
import { playSound } from '../../utils/utils';
import { LAST_SIGNAL_STORIES, SIGNAL_STATIC_SOUND } from './lastSignalStory';
import { createLastSignalScene } from './lastSignalScene';
import LastSignalFallback from './LastSignalFallback';
import './LastSignalCinematic.css';

const LastSignalCinematic = ({
  variant = 'opening',
  dieId,
  faceValues,
  reduceMotion = false,
  onComplete,
}) => {
  const story = LAST_SIGNAL_STORIES[variant];
  const hostRef = useRef(null);
  const dialogRef = useRef(null);
  const completeRef = useRef(onComplete);
  const finishedRef = useRef(false);
  const stopSoundsRef = useRef([]);
  const [chapterIndex, setChapterIndex] = useState(0);
  const [fallback, setFallback] = useState(false);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);
  completeRef.current = onComplete;
  pausedRef.current = paused;
  const finish = () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    stopSoundsRef.current.forEach((stop) => stop());
    stopSoundsRef.current = [];
    completeRef.current?.();
  };
  const finishRef = useRef(finish);
  finishRef.current = finish;

  useEffect(() => {
    const releaseScroll = lockBodyScroll();
    const root = document.getElementById('root');
    const previousInert = root?.inert;
    const previousHidden = root?.getAttribute('aria-hidden');
    const previousFocus = document.activeElement;
    dialogRef.current?.focus({ preventScroll: true });
    if (root) {
      root.inert = true;
      root.setAttribute('aria-hidden', 'true');
    }
    // Reduced motion keeps a still illustration and the complete transcript.
    const scene = reduceMotion
      ? null
      : createLastSignalScene(hostRef.current, { variant, dieId, faceValues });
    setFallback(!scene);
    let elapsed = 0,
      previousTime = null,
      frameId,
      currentChapter = 0;
    let lostContext = false;
    const resetClock = () => {
      previousTime = null;
    };
    document.addEventListener('visibilitychange', resetClock);
    const played = new Set();
    const cue = (key, sound) => {
      if (played.has(key)) return;
      played.add(key);
      const stop = playSound(sound, { preventOverlap: true });
      if (typeof stop === 'function') stopSoundsRef.current.push(stop);
    };
    const animate = (now) => {
      if (finishedRef.current) return;
      // Preserve reading time when the tab is hidden or the scene is paused.
      if (previousTime !== null && !pausedRef.current && !document.hidden)
        elapsed += now - previousTime;
      previousTime = now;
      const time = elapsed / 1000;
      // Render a held frame again after resizing while paused.
      if (scene && !lostContext && !document.hidden && !scene.update(time)) {
        lostContext = true;
        setFallback(true);
      }
      if (!pausedRef.current && !document.hidden) {
        dialogRef.current?.style.setProperty(
          '--signal-progress',
          String(Math.min(1, elapsed / story.duration))
        );
        let nextChapter = currentChapter;
        story.chapters.forEach((chapter, index) => {
          if (time < chapter.at) return;
          nextChapter = index;
          if (chapter.line) cue(`line-${index}`, SIGNAL_STATIC_SOUND);
        });
        if (nextChapter !== currentChapter) {
          currentChapter = nextChapter;
          setChapterIndex(currentChapter);
        }
        story.sounds.forEach(([at, sound], index) => {
          if (time >= at) cue(`sound-${index}`, sound);
        });
        if (elapsed >= story.duration) {
          finishRef.current();
          return;
        }
      }
      frameId = requestAnimationFrame(animate);
    };
    if (!reduceMotion) frameId = requestAnimationFrame(animate);
    return () => {
      cancelAnimationFrame(frameId);
      document.removeEventListener('visibilitychange', resetClock);
      stopSoundsRef.current.forEach((stop) => stop());
      stopSoundsRef.current = [];
      scene?.dispose();
      releaseScroll();
      if (root) {
        root.inert = previousInert;
        if (previousHidden === null) root.removeAttribute('aria-hidden');
        else root.setAttribute('aria-hidden', previousHidden);
      }
      if (previousFocus?.isConnected)
        previousFocus.focus?.({ preventScroll: true });
    };
  }, [variant, dieId, faceValues, reduceMotion, story]);

  const chapter = story.chapters[chapterIndex];
  return createPortal(
    <section
      ref={dialogRef}
      className={`lastSignalCinematic is-${variant} chapter-${chapterIndex} ${
        fallback ? 'is-fallback' : ''
      } ${reduceMotion ? 'is-reduced' : ''} ${paused ? 'is-paused' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label={story.title}
      tabIndex={-1}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          finish();
        }
        if (event.key === 'Tab') {
          const controls = Array.from(
            dialogRef.current.querySelectorAll('button, [tabindex="0"]')
          );
          const first = controls[0],
            last = controls[controls.length - 1];
          if (
            event.shiftKey &&
            (document.activeElement === first ||
              document.activeElement === dialogRef.current)
          ) {
            event.preventDefault();
            last.focus();
          } else if (
            !event.shiftKey &&
            (document.activeElement === last ||
              document.activeElement === dialogRef.current)
          ) {
            event.preventDefault();
            first.focus();
          }
        }
      }}
    >
      <div ref={hostRef} className="lastSignalScene" aria-hidden="true" />
      {fallback && (
        <LastSignalFallback
          variant={variant}
          dieId={dieId}
          faceValues={faceValues}
        />
      )}
      <div className="lastSignalVignette" aria-hidden="true" />
      <div className="lastSignalGrain" aria-hidden="true" />
      <header className="lastSignalHeader">
        <span>COREBOUND / {story.designation}</span>
        <span className="lastSignalTransmission">
          {variant === 'opening' ? 'THE LAST SIGNAL' : 'ORIGIN REACHED'}
        </span>
      </header>
      {reduceMotion ? (
        <div className="lastSignalTranscript" tabIndex={0}>
          <span className="lastSignalEyebrow">{story.title}</span>
          {story.chapters.map((entry) => (
            <div key={entry.at}>
              <h2>{entry.title}</h2>
              {entry.detail && <p>{entry.detail}</p>}
              {entry.line && (
                <p>
                  <strong>{entry.speaker}</strong> “{entry.line}”
                </p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div
          className="lastSignalAnnouncement"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <div key={chapterIndex} className="lastSignalCaption">
            <span className="lastSignalEyebrow">{chapter.label}</span>
            <h2>{chapter.title}</h2>
            {chapter.detail && (
              <p className="lastSignalDetail">{chapter.detail}</p>
            )}
            {chapter.line && (
              <div className="lastSignalDialogue">
                <span className="lastSignalWaveform" aria-hidden="true">
                  {Array.from({ length: 17 }, (_, index) => (
                    <i key={index} style={{ '--bar': index }} />
                  ))}
                </span>
                <div>
                  <span className="lastSignalSpeaker">{chapter.speaker}</span>
                  <p>“{chapter.line}”</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <footer className="lastSignalFooter">
        <span className="lastSignalChapter" aria-hidden="true">
          {reduceMotion
            ? 'TRANSMISSION LOG'
            : `${String(chapterIndex + 1).padStart(2, '0')} / ${String(
                story.chapters.length
              ).padStart(2, '0')}`}
        </span>
        <div className="lastSignalControls">
          {!reduceMotion && (
            <button
              type="button"
              aria-pressed={paused}
              onClick={() => {
                if (!paused) {
                  stopSoundsRef.current.forEach((stop) => stop());
                  stopSoundsRef.current = [];
                }
                setPaused((value) => !value);
              }}
            >
              {paused ? 'Resume scene' : 'Pause scene'}
            </button>
          )}
          <button type="button" onClick={finish}>
            {reduceMotion ? story.continueLabel : story.skipLabel}{' '}
            <span aria-hidden="true">→</span>
          </button>
        </div>
      </footer>
      {!reduceMotion && (
        <div className="lastSignalProgress" aria-hidden="true">
          <i />
        </div>
      )}
    </section>,
    document.body
  );
};

export default LastSignalCinematic;
